import React from "react";
import StepSuccess from "../components/StepSuccess";
import StepError from "../components/StepError";
import ModuleSelect from "../components/ModuleSelect";
import Invoice from "../components/Invoice";
import Payment from "../components/Payment";
import Payout from "../components/Payout";
import Subscription from "../components/Subscription";
import {motion, AnimatePresence} from "framer-motion";
import {t} from "i18next";
import {WizardStepKey} from "../helpers/onboardingModules";

const STEP_COMPONENTS: Record<string, React.FC<{formik: any}>> = {
    modules: ModuleSelect,
    invoice: Invoice,
    payment: Payment,
    payout: Payout,
    subscription: Subscription,
    success: StepSuccess,
    error: StepError,
};

/** API/DB often returns null — coerce so Yup shows required messages instead of type errors. */
const requiredSelect = (yup: any, message: string) =>
    yup
        .mixed()
        .transform((value: unknown) => (value === null || value === undefined ? '' : value))
        .test('required-select', message, (value: unknown) => value !== '' && value !== null && value !== undefined);

const choose = (fieldKey: string) =>
    t("common.setup.pleaseChoose", {field: t(fieldKey)});

const enter = (fieldKey: string) =>
    t("common.setup.pleaseEnter", {field: t(fieldKey)});

export const getValidation = (stepKey: WizardStepKey, yup: any) => {
    switch (stepKey) {
        case 'modules':
            return yup.object().shape({
                enabled_sync_modules: yup
                    .array()
                    .nullable()
                    .transform((value: unknown) => (Array.isArray(value) ? value : []))
                    .of(yup.string())
                    .min(1, t("common.setup.modulesRequired")),
            });
        case 'invoice':
            return yup.object().shape({
                stripe_invoice_source: yup
                    .string()
                    .nullable()
                    .transform((value: unknown) => (value == null ? '' : String(value)))
                    .oneOf(['invoice', 'checkout'], choose("economic.invoiceSource"))
                    .required(choose("economic.invoiceSource")),
                stripe_checkout_prices_include_vat: yup.boolean(),
                stripe_checkout_included_vat_rate: yup.number().when(
                    'stripe_checkout_prices_include_vat',
                    {
                        is: true,
                        then: yup.number().nullable().min(0).max(100).required(enter("economic.checkoutIncludedVatRate")),
                        otherwise: yup.number().nullable(),
                    }
                ),
            });
        case 'payment':
            return yup.object().shape({
                economic_payment_cashbook_number: requiredSelect(yup, choose("economic.cashbook")),
                economic_payment_bank_account_number: requiredSelect(yup, choose("economic.bankAccount")),
                economic_payment_credit_account_number: yup.mixed().when(
                    "economic_payment_matching_invoice",
                    {
                        is: false,
                        then: requiredSelect(yup, choose("economic.creditAccount")),
                        otherwise: yup.mixed().nullable(),
                    }
                ),
                economic_payment_fee_account_number: requiredSelect(yup, choose("economic.feeAccount")),
                economic_payment_difference_currency_account_number: requiredSelect(
                    yup,
                    choose("economic.differenceAccountCurrency")
                ),
            });
        case 'payout':
            return yup.object().shape({
                economic_payout_cashbook_number: requiredSelect(yup, choose("economic.cashbook")),
                economic_payout_bank_account_number: requiredSelect(yup, choose("economic.bankAccount")),
                economic_payout_credit_account_number: requiredSelect(yup, choose("economic.payoutCreditAccount")),
                // Fee optional in V2 matching — without fee account, sync books 2 lines (net only).
                economic_payout_fee_account_number: yup.mixed().nullable(),
                economic_payout_difference_currency_account_number: requiredSelect(
                    yup,
                    choose("economic.differenceAccountCurrency")
                ),
            });
        case 'subscription':
            return yup.object().shape({
                economic_subscription_cashbook_number: requiredSelect(yup, choose("economic.cashbook")),
                economic_subscription_balance_account_number: requiredSelect(yup, choose("economic.balanceAccount")),
                economic_subscription_sales_account_number: requiredSelect(yup, choose("economic.salesAccount")),
            });
        default:
            return yup.object().shape({});
    }
};

export const SetupConfig = (
    stepKey: WizardStepKey,
    direction: number,
    formik: any,
    labelKey?: string,
) => {
    const Component = STEP_COMPONENTS[stepKey] || ModuleSelect;

    return (
        <React.Fragment>
            <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                    key={stepKey}
                    initial={{x: direction * 200, opacity: 0}}
                    animate={{x: 0, opacity: 1}}
                    exit={{x: -direction * 200, opacity: 0}}
                    transition={{duration: 0.3, ease: "easeInOut"}}
                    className="step-content"
                >
                    {labelKey && stepKey !== 'success' && stepKey !== 'error' ? (
                        <h2 className="mb-4 mt-3 fs-32 cl-orange-zp fw-bold">
                            {`${t("common.setup.setupPrefix")} `}
                            <span className="fw-400">{String(t(labelKey))}</span>
                        </h2>
                    ) : null}
                    <Component formik={formik}/>
                </motion.div>
            </AnimatePresence>
        </React.Fragment>
    );
};
